import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const LandingHero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#0F172A]">
      {/* Gradient mesh background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#F59E0B]/5 to-transparent rounded-full" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      <div className="container relative z-10 mx-auto px-4 py-20">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
          {/* Content Left */}
          <div className="space-y-8 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
            {/* Badge */}
            <div className="flex justify-center lg:justify-start animate-fade-up">
              <div className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 backdrop-blur-sm">
                <svg className="h-4 w-6 flex-shrink-0 rounded-[2px]" viewBox="0 0 30 20">
                  <rect width="10" height="20" fill="#002395" />
                  <rect x="10" width="10" height="20" fill="#FFFFFF" />
                  <rect x="20" width="10" height="20" fill="#ED2939" />
                </svg>
                <span className="text-sm font-medium text-white/70">
                  Solution BTP 100% Française
                </span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="animate-fade-up [animation-delay:100ms]">
              <span className="block text-[2.75rem] sm:text-5xl lg:text-6xl xl:text-[4rem] font-extrabold tracking-tight text-white leading-[1.1]">
                La rentabilité de vos
              </span>
              <span className="block text-[2.75rem] sm:text-5xl lg:text-6xl xl:text-[4rem] font-extrabold tracking-tight leading-[1.1] mt-2">
                <span className="bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-clip-text text-transparent bg-[length:200%_auto] animate-shimmer">
                  chantiers BTP
                </span>
              </span>
              <span className="block text-[2.75rem] sm:text-5xl lg:text-6xl xl:text-[4rem] font-extrabold tracking-tight text-white/90 leading-[1.1] mt-2">
                en un coup d'oeil.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-white/50 leading-relaxed max-w-xl mx-auto lg:mx-0 animate-fade-up [animation-delay:200ms]">
              Rentabilité temps réel. Anticipation des coûts retards. Planification des équipes. Tout ce qu'il vous faut pour ne plus perdre d'argent sur vos chantiers.
            </p>

            {/* CTA Button */}
            <div className="flex justify-center lg:justify-start pt-4 animate-fade-up [animation-delay:300ms] px-2 sm:px-0">
              <Button
                size="lg"
                className="h-12 sm:h-14 px-5 sm:px-8 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base sm:text-lg gap-2 sm:gap-3 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_8px_40px_rgba(245,158,11,0.4)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_12px_50px_rgba(245,158,11,0.5)] hover:scale-[1.02] transition-all duration-300 whitespace-nowrap"
                asChild
              >
                <Link to="/auth?mode=signup">
                  Essayer gratuitement 14 jours
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-col items-center lg:flex-row lg:justify-start gap-4 lg:gap-8 pt-6 animate-fade-up [animation-delay:350ms]">
              <div className="text-center">
                <span className="text-xl md:text-2xl font-bold text-[#F59E0B]">4h/semaine</span>
                <span className="text-sm text-white/40 ml-2">gagnées sur le suivi</span>
              </div>
              <span className="hidden lg:block text-white/20">·</span>
              <div className="text-center">
                <span className="text-xl md:text-2xl font-bold text-[#F59E0B]">+18%</span>
                <span className="text-sm text-white/40 ml-2">de marge moyenne</span>
              </div>
              <span className="hidden lg:block text-white/20">·</span>
              <div className="text-center">
                <span className="text-xl md:text-2xl font-bold text-[#F59E0B]">100%</span>
                <span className="text-sm text-white/40 ml-2">de visibilité sur vos coûts</span>
              </div>
            </div>

            {/* Social Proof */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-x-8 pt-6 animate-fade-up [animation-delay:400ms]">
              <div className="text-sm">
                <span className="text-white/80 font-semibold">280+</span>
                <span className="text-white/40 ml-1">TPE/PME BTP</span>
              </div>
              <div className="h-px w-12 sm:h-4 sm:w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-[#F59E0B]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-white/40">4.9/5</span>
              </div>
            </div>
          </div>

          {/* Product Preview Right */}
          <div className="relative animate-fade-up [animation-delay:200ms] lg:animate-slide-in-right">
            {/* Glow behind */}
            <div className="absolute -inset-8 bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-[#1A365D]/10 blur-3xl rounded-3xl" />

            {/* Main card with gradient border */}
            <div className="relative rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
              <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.02] border-b border-white/[0.05]">
                  <div className="flex gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="px-3 py-1 rounded-md bg-white/[0.03] text-[11px] text-white/30 font-mono">
                      app.b8ild.fr/dashboard
                    </div>
                  </div>
                </div>

                {/* Dashboard content - Gestion financière */}
                <div className="p-5 space-y-4">
                  {/* Projet Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Chantier</p>
                      <p className="text-base font-semibold text-white mt-0.5">Villa Durand</p>
                    </div>
                    <span className="text-[10px] text-white/30">26 janv. → 29 janv. 2026</span>
                  </div>

                  {/* Main KPIs - Marge Actuelle + Rentabilité Finale */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.04] p-2 sm:p-3">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-wider text-white/30 font-medium">Marge Actuelle</p>
                      <p className="text-2xl sm:text-3xl font-bold text-[#10B981] mt-1">74.3%</p>
                      <p className="text-[9px] sm:text-[10px] text-white/30 mt-1">à ce jour</p>
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[8px] sm:text-[9px] text-[#10B981] font-medium">
                        <span>🏆</span> Renta Exceptionnelle
                      </span>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.04] p-2 sm:p-3">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-wider text-white/30 font-medium">Renta Finale Estimée</p>
                      <p className="text-2xl sm:text-3xl font-bold text-[#F59E0B] mt-1">46.1%</p>
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[8px] sm:text-[9px] text-[#F59E0B] font-medium">
                        🏆 Renta Exceptionnelle
                      </span>
                    </div>
                  </div>

                  {/* Small KPIs */}
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { label: "Budget HT", value: "4 545€", color: "text-white" },
                      { label: "Coût équipe", value: "511€", color: "text-white" },
                      { label: "Coûts fixes", value: "659€", color: "text-white" },
                      { label: "Jours", value: "1/3.5", color: "text-white" },
                    ].map((kpi, i) => (
                      <div key={i} className="rounded-lg bg-white/[0.02] border border-white/[0.03] p-1.5 text-center">
                        <p className="text-[7px] uppercase tracking-wider text-white/30 font-medium whitespace-nowrap">{kpi.label}</p>
                        <p className={`text-xs font-bold ${kpi.color} mt-0.5 whitespace-nowrap`}>{kpi.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Marge details */}
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                    <div className="rounded-lg bg-white/[0.02] border-l-2 border-l-[#10B981] border border-white/[0.03] p-1.5 sm:p-2">
                      <p className="text-[7px] sm:text-[8px] uppercase tracking-wider text-white/30 whitespace-nowrap">Marge à date</p>
                      <p className="text-sm sm:text-base font-bold text-[#10B981]">3 375€</p>
                    </div>
                    <div className="rounded-lg bg-white/[0.02] border-l-2 border-l-[#F59E0B] border border-white/[0.03] p-1.5 sm:p-2">
                      <p className="text-[7px] sm:text-[8px] uppercase tracking-wider text-white/30 whitespace-nowrap">Jours rentables</p>
                      <p className="text-sm sm:text-base font-bold text-[#F59E0B]">6</p>
                      <p className="hidden sm:block text-[8px] text-white/30">Jours restants avant marge nulle</p>
                    </div>
                    <div className="rounded-lg bg-white/[0.02] border-l-2 border-l-[#10B981] border border-white/[0.03] p-1.5 sm:p-2">
                      <p className="text-[7px] sm:text-[8px] uppercase tracking-wider text-white/30 whitespace-nowrap">Marge finale</p>
                      <p className="text-sm sm:text-base font-bold text-[#10B981]">2 097€</p>
                      <p className="hidden sm:block text-[8px] text-[#10B981]">(46.1%)</p>
                    </div>
                  </div>

                  {/* Jour Critique Visualization */}
                  <div className="rounded-xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.04] p-3">
                    <p className="text-[10px] text-white/40 font-medium text-center mb-2">Visualisation du Jour Critique</p>
                    <div className="h-2.5 rounded-full bg-white/[0.05] overflow-hidden flex">
                      <div className="h-full bg-gradient-to-r from-[#10B981] to-[#10B981]" style={{ width: '20%' }} />
                      <div className="h-full bg-gradient-to-r from-[#EF4444] to-[#EF4444]" style={{ width: '8%' }} />
                      <div className="h-full bg-gradient-to-r from-[#F59E0B] to-[#F59E0B]" style={{ width: '50%' }} />
                      <div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#3B82F6]" style={{ width: '22%' }} />
                    </div>
                    <div className="flex justify-between mt-2 text-[8px] text-white/30">
                      <span>0</span>
                      <span>1</span>
                      <span>2</span>
                      <span>3</span>
                      <span>4</span>
                      <span>5</span>
                      <span>6</span>
                      <span>7</span>
                    </div>
                    <div className="flex justify-center gap-4 mt-2">
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-[#10B981]" />
                        <span className="text-[8px] text-white/40">Progression</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-[#F59E0B]" />
                        <span className="text-[8px] text-white/40">Jours de marge</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-2 rounded-full bg-[#EF4444]" />
                        <span className="text-[8px] text-white/40">Jour critique</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating element - Jour Critique */}
            <div className="absolute -bottom-4 -left-4 rounded-xl p-[1px] bg-gradient-to-br from-[#F59E0B]/50 to-[#F59E0B]/10 animate-float">
              <div className="rounded-xl bg-[#0F0F14]/95 backdrop-blur-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#F59E0B]">2 févr.</p>
                    <p className="text-[10px] text-white/40">jour critique</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
